import { useDebounce } from '@uidotdev/usehooks';
import { Filter, Search, X } from 'lucide-react';
import * as React from 'react';

import { Card, Input } from '@/design-system/components/ui';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/design-system/components/ui/sheet';
import { cn } from '@/design-system/lib/utils';

import { FilterChip } from './FilterChip';
import type {
  FilterChipDateRangeDef,
  FilterChipDef,
  FilterToolbarSchema,
  FilterToolbarSearchDef,
  FilterToolbarValue,
} from './types';
import { getChipDefault, isChipActive, resolveDateKeys, useFilterToolbarState } from './useFilterToolbarState';

/**
 * `FilterToolbar` — the always-visible filter bar.
 *
 * Schema-driven: pass a `schema` and the toolbar handles state, URL sync, and
 * layout. State is mirrored to the URL via `nuqs`. Default/empty values are
 * stripped from the query string so an "all clear" toolbar produces no query
 * params.
 *
 * Responsive layout:
 *   - **≥ md (tablet + desktop)** — the inline toolbar: search at the start,
 *     chips inline, the optional date chip pinned right, "Clear all" surfaces
 *     when ≥1 filter is active.
 *   - **< md (mobile)** — collapsed to a single "Filters" button. Tapping it
 *     opens a right-side `Sheet` containing the same controls stacked
 *     vertically as form-style fields. Both views share the same state hook,
 *     so URL state stays consistent across viewport changes.
 *
 * Multi-select: opt-in per chip via `multi: true` on `combobox` chips.
 */
export interface FilterToolbarProps {
  schema: FilterToolbarSchema;
  /** Optional className applied to the toolbar shell. */
  className?: string;
  /** Called whenever the active filter count changes. */
  onActiveCountChange?: (count: number) => void;
}

export const FilterToolbar = ({ schema, className, onActiveCountChange }: FilterToolbarProps) => {
  const state = useFilterToolbarState(schema);
  const { activeCount } = state;

  React.useEffect(() => {
    onActiveCountChange?.(activeCount);
  }, [activeCount, onActiveCountChange]);

  return (
    <>
      {/* ≥ md — inline toolbar */}
      <div className={cn('tw:hidden tw:md:block', className)}>
        <DesktopToolbar schema={schema} state={state} />
      </div>

      {/* < md — Filters button + Sheet */}
      <div className={cn('tw:md:hidden', className)}>
        <MobileSheet schema={schema} state={state} />
      </div>
    </>
  );
};

// ── Desktop toolbar ───────────────────────────────────────────────────────

interface ViewProps {
  schema: FilterToolbarSchema;
  state: ReturnType<typeof useFilterToolbarState>;
}

const DesktopToolbar = ({ schema, state }: ViewProps) => {
  const { value, set, setMany, clearAll, activeCount, isActive } = state;

  return (
    <Card variant="subcard" className="tw:flex tw:flex-row tw:items-center tw:gap-2 tw:flex-wrap tw:p-2">
      <ToolbarLabel />
      {schema.search && (
        <>
          <ToolbarSearch
            def={schema.search}
            value={(value[schema.search.name] as string) ?? ''}
            onCommit={(next) => set(schema.search!.name, next)}
          />
          {schema.chips.length > 0 && <ToolbarDivider />}
        </>
      )}

      {schema.chips.map((chip) => (
        <ChipSlot key={chip.name} chip={chip} value={value} state={state} layout="chip" />
      ))}

      {schema.date && (
        <FilterChip
          def={{ ...schema.date, type: 'date-range' }}
          value=""
          rangeValue={readDateRange(schema.date, value)}
          defaultValue=""
          isActive={isActive(schema.date.name)}
          onChange={() => undefined}
          onDateRangeChange={(range) => {
            const { fromKey, toKey } = resolveDateKeys(schema.date as FilterChipDateRangeDef);
            setMany({
              [fromKey]: range.from ? range.from.toISOString() : null,
              [toKey]: range.to ? range.to.toISOString() : null,
            });
          }}
        />
      )}

      {activeCount > 0 && <ClearAllButton onClear={clearAll} className="tw:ml-auto" />}
    </Card>
  );
};

// ── Mobile sheet ──────────────────────────────────────────────────────────

const MobileSheet = ({ schema, state }: ViewProps) => {
  const { value, set, setMany, clearAll, activeCount, isActive } = state;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          className={cn(
            'tw:inline-flex tw:items-center tw:gap-2 tw:h-10 tw:px-4 tw:rounded-[10px] tw:border tw:border-border tw:bg-card tw:text-sm tw:font-medium tw:font-inter tw:cursor-pointer',
            'tw:transition-colors tw:hover:bg-muted tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-fuul-blue',
          )}
        >
          <Filter className="tw:h-4 tw:w-4 tw:text-muted-foreground" aria-hidden="true" />
          <span>Filters</span>
          {activeCount > 0 && (
            <span
              className="tw:inline-flex tw:items-center tw:justify-center tw:h-5 tw:min-w-5 tw:px-1.5 tw:rounded-full tw:bg-fuul-blue tw:text-white tw:text-xs tw:font-semibold"
              aria-label={`${activeCount} active filter${activeCount === 1 ? '' : 's'}`}
            >
              {activeCount}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="tw:w-full tw:max-w-sm tw:flex tw:flex-col tw:gap-0 tw:p-0">
        <SheetHeader className="tw:p-4 tw:border-b tw:border-border">
          <SheetTitle>Filters</SheetTitle>
        </SheetHeader>
        <div className="tw:flex-1 tw:overflow-y-auto tw:p-4 tw:flex tw:flex-col tw:gap-3">
          {schema.search && (
            <ToolbarSearch
              def={schema.search}
              value={(value[schema.search.name] as string) ?? ''}
              onCommit={(next) => set(schema.search!.name, next)}
              variant="field"
            />
          )}

          {schema.chips.map((chip) => (
            <ChipSlot key={chip.name} chip={chip} value={value} state={state} layout="field" />
          ))}

          {schema.date && (
            <FilterChip
              def={{ ...schema.date, type: 'date-range' }}
              value=""
              rangeValue={readDateRange(schema.date, value)}
              defaultValue=""
              isActive={isActive(schema.date.name)}
              onChange={() => undefined}
              layout="field"
              onDateRangeChange={(range) => {
                const { fromKey, toKey } = resolveDateKeys(schema.date as FilterChipDateRangeDef);
                setMany({
                  [fromKey]: range.from ? range.from.toISOString() : null,
                  [toKey]: range.to ? range.to.toISOString() : null,
                });
              }}
            />
          )}
        </div>
        {activeCount > 0 && (
          <div className="tw:p-4 tw:border-t tw:border-border">
            <ClearAllButton onClear={clearAll} className="tw:w-full tw:justify-center tw:h-10" />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ── Internals ──────────────────────────────────────────────────────────────

interface ChipSlotProps {
  chip: FilterChipDef;
  value: FilterToolbarValue;
  state: ReturnType<typeof useFilterToolbarState>;
  layout: 'chip' | 'field';
}

const ChipSlot = ({ chip, value, state, layout }: ChipSlotProps) => {
  const chipValue = value[chip.name] as string | string[];
  const fallback = getChipDefault(chip);
  return (
    <FilterChip
      def={chip}
      value={chipValue}
      defaultValue={fallback}
      isActive={isChipActive(chip, chipValue)}
      onChange={(next) => state.set(chip.name, next)}
      layout={layout}
    />
  );
};

const ToolbarDivider = () => <span aria-hidden="true" className="tw:h-6 tw:w-px tw:bg-border tw:mx-1 tw:shrink-0" />;

const ToolbarLabel = () => (
  <span className="tw:pl-2 tw:pr-1 tw:text-sm tw:font-medium tw:font-inter tw:text-muted-foreground tw:shrink-0">
    Filters
  </span>
);

interface ToolbarSearchProps {
  def: FilterToolbarSearchDef;
  value: string;
  onCommit: (next: string | null) => void;
  /** `inline` (default) — fixed 280px width for the desktop toolbar. `field` —
   * full-width input for the mobile sheet. */
  variant?: 'inline' | 'field';
}

const ToolbarSearch = ({ def, value, onCommit, variant = 'inline' }: ToolbarSearchProps) => {
  const [local, setLocal] = React.useState(value);
  const debounceMs = def.debounceMs ?? 600;
  const debounced = useDebounce(local, debounceMs);

  // Push debounced changes to URL.
  React.useEffect(() => {
    onCommit(debounced || null);
  }, [debounced]);

  // Sync from URL → local (e.g. clearAll).
  React.useEffect(() => {
    setLocal(value);
  }, [value]);

  return (
    <div className={cn('tw:flex', variant === 'inline' ? 'tw:w-[280px] tw:min-w-[200px] tw:shrink' : 'tw:w-full')}>
      <Input
        type="text"
        size={variant === 'inline' ? 'sm' : 'default'}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        placeholder={def.placeholder ?? 'Search…'}
        leftSlot={<Search className="tw:h-4 tw:w-4 tw:text-muted-foreground" aria-hidden="true" />}
        rightSlot={
          local.length > 0 ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setLocal('')}
              className="tw:inline-flex tw:items-center tw:justify-center tw:h-5 tw:w-5 tw:rounded-full tw:bg-muted tw:text-muted-foreground tw:hover:bg-muted/80 tw:transition-colors"
            >
              <X className="tw:h-3 tw:w-3" strokeWidth={2.5} />
            </button>
          ) : undefined
        }
      />
    </div>
  );
};

interface ClearAllButtonProps {
  onClear: () => void;
  className?: string;
}

const ClearAllButton = ({ onClear, className }: ClearAllButtonProps) => (
  <button
    type="button"
    onClick={onClear}
    className={cn(
      'tw:inline-flex tw:items-center tw:gap-1 tw:h-9 tw:px-3 tw:rounded-[10px]',
      'tw:text-sm tw:font-medium tw:font-inter tw:text-muted-foreground',
      'tw:bg-transparent tw:border tw:border-transparent tw:cursor-pointer',
      'tw:transition-colors tw:hover:bg-error-main/8 tw:hover:text-error-main',
      className,
    )}
  >
    Clear all
  </button>
);

const readDateRange = (
  def: FilterChipDateRangeDef,
  value: FilterToolbarValue,
): { from: Date | null; to: Date | null } => {
  const { fromKey, toKey } = resolveDateKeys(def);
  const fromStr = (value[fromKey] as string) ?? '';
  const toStr = (value[toKey] as string) ?? '';
  const parseDate = (s: string): Date | null => {
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  };
  return { from: parseDate(fromStr), to: parseDate(toStr) };
};
